import React, { useState, useEffect } from 'react'
import MoodTracker from './MoodTracker.jsx'
import ReadingSessionModal from '../Reading/ReadingSessionModal.jsx'
import ConfirmDialog from '../../components/ConfirmDialog.jsx'
import { getDailyQuote, getGreeting } from '../../utils/quotes.js'
import { todayShort, todayISO, calcStreak } from '../../utils/dates.js'
import * as api from '../../services/api.js'
import Card from '../../components/Card.jsx'
import { COMPANIONS } from '../Activities/companionData.js'
import { useAuth } from '../../context/AuthContext.jsx'

function personalizeGreeting(text, name) {
  return `${text}, ${name}.`
}

// ── Section definitions ──────────────────────────────────────────────────────

const SECTIONS = [
  { id: 'checkin',    label: 'Check-in' },
  { id: 'focus',      label: "Today's Focus" },
  { id: 'tasks',      label: 'Tasks' },
  { id: 'reflection', label: 'Reflection' },
  { id: 'progress',   label: 'Progress' },
]

// ── Quiet Moment prompts ─────────────────────────────────────────────────────

const QUIET_PROMPTS = [
  'What was one small moment today that felt okay?',
  'Did anything make you smile, even briefly?',
  'What did your body need most today?',
  'Is there something you did just for yourself lately?',
  "What's one thing you noticed around you today?",
  'What would feel like a gentle treat this week?',
  'When did time pass without you noticing it?',
  'What is one thing you are quietly proud of?',
  'What has been weighing on you? Name it gently.',
  'What does your body feel like right now — really?',
  'Who or what brought you a moment of warmth recently?',
  'What would you tell yourself from last week?',
  'Is there something you have been putting off that you could do kindly today?',
  'What colour feels like today?',
  'What small act of care did you extend to yourself this week?',
  'What do you want to remember about right now?',
  'What has surprised you lately?',
  'When did you feel most like yourself today?',
  'What is something ordinary you are quietly grateful for?',
  'What unfinished thought keeps returning to you?',
  'What does rest look like for you today?',
  'What are you learning, even slowly?',
  'What would you do differently if you were being kinder to yourself?',
  'What sounds surround you right now?',
  'Name one thing that is going well, however small.',
  'What has changed since last month — inside you?',
  'What does your energy feel like today?',
  'Is there something you need to say out loud — even just here?',
  'What is worth protecting in your life right now?',
  'What would "enough" look like today?',
]

function pickQuietPrompt(offset = 0) {
  const now = new Date()
  const dayOfYear = Math.floor((now - new Date(now.getFullYear(), 0, 0)) / 86400000)
  return QUIET_PROMPTS[(dayOfYear + offset) % QUIET_PROMPTS.length]
}

// ── Mood names for progress snapshot ─────────────────────────────────────────

const MOOD_NAMES = ['Sad', 'Anxious', 'Calm', 'Happy', 'Excited']

// ── Task TTL (matches TodoKanban) ─────────────────────────────────────────────

const DONE_TTL = 12 * 60 * 60 * 1000

// ─────────────────────────────────────────────────────────────────────────────
// Section nav
// ─────────────────────────────────────────────────────────────────────────────

function DashboardSectionNav({ activeSection }) {
  function scrollTo(id) {
    const el = document.getElementById(id)
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  return (
    <div className="dashboard-section-nav">
      {SECTIONS.map(s => (
        <button
          key={s.id}
          className={`dashboard-nav-btn${activeSection === s.id ? ' active' : ''}`}
          onClick={() => scrollTo(s.id)}
        >
          {s.label}
        </button>
      ))}
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// Today's Focus — read-only challenge preview
// ─────────────────────────────────────────────────────────────────────────────

const DEFAULT_WALK = { companion: 'carl', goal: 5000, logs: {} }

function TodaysFocus({ onNavigate }) {
  const [walk, setWalk]         = useState(null)
  const [loading, setLoading]   = useState(true)
  const [failed, setFailed]     = useState(false)
  const [stepInput, setStepInput] = useState('')
  const [stepSaved, setStepSaved] = useState(false)

  useEffect(() => {
    api.getWalkState('walking')
      .then(d => setWalk(d || null))
      .catch(() => setFailed(true))
      .finally(() => setLoading(false))
  }, [])

  if (loading) return null

  if (failed || !walk) {
    return (
      <Card label="Today's Focus" title="Active Challenge">
        <p style={{ fontSize: '0.88rem', color: 'var(--stone)', fontStyle: 'italic', marginBottom: '0.75rem' }}>
          No active challenge — explore some?
        </p>
        <button
          className="ghost-btn"
          style={{ fontSize: '0.8rem' }}
          onClick={() => onNavigate && onNavigate('challenges/explore')}
        >
          Explore challenges →
        </button>
      </Card>
    )
  }

  const data       = { ...DEFAULT_WALK, ...walk }
  const companion  = COMPANIONS[data.companion] ?? COMPANIONS.carl
  const today      = todayISO()
  const compLog    = data.logs?.[companion.id] ?? []
  const todaySteps = (compLog.find(e => e.date === today) ?? { steps: 0 }).steps
  const goalPct    = Math.min(Math.round((todaySteps / data.goal) * 100), 100)

  function logSteps() {
    const steps = parseInt(stepInput, 10)
    if (!steps || steps < 0) return
    const updatedLogs   = { ...(data.logs || {}) }
    const logs          = [...(updatedLogs[companion.id] || [])]
    const idx           = logs.findIndex(e => e.date === today)
    if (idx >= 0) logs[idx] = { ...logs[idx], steps }
    else logs.unshift({ date: today, steps })
    updatedLogs[companion.id] = logs
    const updated = { ...data, logs: updatedLogs }
    setWalk(updated)
    setStepInput('')
    setStepSaved(true)
    setTimeout(() => setStepSaved(false), 2000)
    api.saveWalkState('walking', updated)
  }

  return (
    <Card label="Today's Focus" title="Active Challenge">
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '1rem', flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <p style={{ fontSize: '1rem', fontFamily: 'var(--font-serif)', color: 'var(--ink)', marginBottom: '0.3rem' }}>
            {companion.cardEmoji} {companion.name}
          </p>
          <p style={{ fontSize: '0.85rem', color: 'var(--stone)', marginBottom: '0.5rem' }}>
            {todaySteps.toLocaleString()} / {data.goal.toLocaleString()} steps today
          </p>
          <div style={{ background: 'var(--surf)', borderRadius: '20px', height: '6px', overflow: 'hidden', maxWidth: '200px' }}>
            <div style={{ height: '100%', borderRadius: '20px', background: 'var(--accent)', width: `${goalPct}%`, transition: 'width 0.4s ease' }} />
          </div>
          {goalPct >= 100 && (
            <p style={{ fontSize: '0.78rem', color: 'var(--sage)', marginTop: '0.35rem', fontStyle: 'italic' }}>
              Daily goal reached! ✓
            </p>
          )}
          <div style={{ display: 'flex', gap: '8px', marginTop: '0.75rem', alignItems: 'center', flexWrap: 'wrap' }}>
            <input
              className="field-input"
              type="number"
              min="0"
              placeholder="Log steps…"
              value={stepInput}
              onChange={e => setStepInput(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') logSteps() }}
              style={{ maxWidth: '120px', textAlign: 'center' }}
            />
            <button className="ghost-btn" style={{ fontSize: '0.8rem' }} onClick={logSteps}>
              Log
            </button>
            {stepSaved && (
              <span style={{ fontSize: '0.8rem', color: 'var(--sage)', fontStyle: 'italic' }}>Saved ✓</span>
            )}
          </div>
        </div>
        <button
          className="ghost-btn"
          style={{ flexShrink: 0, fontSize: '0.8rem', whiteSpace: 'nowrap' }}
          onClick={() => onNavigate && onNavigate('challenges')}
        >
          Continue →
        </button>
      </div>
    </Card>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// Reading prompt — log a session from Today
// ─────────────────────────────────────────────────────────────────────────────

function ReadingPrompt({ onNavigate }) {
  const [books, setBooks]           = useState([])
  const [sessionBook, setSessionBook] = useState(null)

  useEffect(() => { api.getBooks().then(rows => setBooks(rows || [])) }, [])

  const reading = books.filter(b => b.status === 'reading')
  if (reading.length === 0) return null

  const book = reading[0]

  return (
    <>
      <Card label="Reading" title={book.title}>
        <p style={{ fontSize: '0.85rem', color: 'var(--stone)', marginBottom: '0.75rem' }}>
          {book.author ? `by ${book.author} — ` : ''}Did you read today?
        </p>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button
            className="save-btn ready"
            style={{ fontSize: '0.8rem', padding: '7px 16px' }}
            onClick={() => setSessionBook(book)}
          >
            Log session
          </button>
          <button
            className="ghost-btn"
            style={{ fontSize: '0.8rem' }}
            onClick={() => onNavigate && onNavigate('reading')}
          >
            View library →
          </button>
        </div>
      </Card>
      {sessionBook && (
        <ReadingSessionModal
          book={sessionBook}
          onClose={() => setSessionBook(null)}
          onSaved={() => {}}
        />
      )}
    </>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// Dashboard Task List — simple checklist with notes + archive
// ─────────────────────────────────────────────────────────────────────────────

const ARCHIVE_TTL = 30 * 24 * 60 * 60 * 1000

function TaskItem({ task, onToggle, onRemove, onNotesChange }) {
  const [notesOpen, setNotesOpen] = useState(false)
  const [notes, setNotes]         = useState(task.notes || '')
  const isDone = task.col === 'done'

  function handleNotesBlur() {
    if (notes !== (task.notes || '')) onNotesChange(task.id, notes)
  }

  return (
    <div>
      <div
        className="dashboard-task-item"
        style={{
          display: 'flex', alignItems: 'center', gap: '10px',
          padding: '8px 10px', background: 'var(--surf)',
          borderRadius: notesOpen ? '10px 10px 0 0' : '10px',
          opacity: isDone ? 0.6 : 1, transition: 'opacity 0.2s',
        }}
      >
        <input
          type="checkbox"
          checked={isDone}
          onChange={() => onToggle(task.id)}
          style={{ width: 16, height: 16, cursor: 'pointer', flexShrink: 0, accentColor: 'var(--accent)' }}
        />
        <span style={{
          flex: 1, fontSize: '0.88rem', lineHeight: 1.5,
          textDecoration: isDone ? 'line-through' : 'none',
          color: isDone ? 'var(--stone)' : 'var(--ink)',
          wordBreak: 'break-word',
        }}>
          {task.text}
        </span>
        <button
          onClick={() => setNotesOpen(v => !v)}
          title="Notes"
          style={{
            background: 'none', border: 'none', cursor: 'pointer',
            fontSize: '12px', flexShrink: 0, padding: 0, lineHeight: 1,
            color: task.notes ? 'var(--accent)' : 'var(--stone)',
            opacity: notesOpen || task.notes ? 1 : 0, transition: 'opacity 0.15s',
          }}
          className="task-del-btn"
        >
          📝
        </button>
        <button
          className="task-del-btn"
          onClick={() => onRemove(task.id)}
          title="Delete"
          style={{ background: 'none', border: 'none', color: 'var(--stone)', cursor: 'pointer', fontSize: '11px', flexShrink: 0, opacity: 0, transition: 'opacity 0.15s', padding: 0, lineHeight: 1 }}
        >
          ✕
        </button>
      </div>
      {notesOpen && (
        <textarea
          className="task-notes-area"
          rows={2}
          placeholder="Add a note…"
          value={notes}
          onChange={e => setNotes(e.target.value)}
          onBlur={handleNotesBlur}
          style={{
            display: 'block', width: '100%', boxSizing: 'border-box',
            background: 'var(--surf)', border: '1px solid var(--stone)',
            borderTop: 'none', borderRadius: '0 0 10px 10px',
            padding: '6px 10px', fontSize: '0.8rem', color: 'var(--ink)',
            resize: 'none', outline: 'none', lineHeight: 1.5,
          }}
        />
      )}
    </div>
  )
}

function DashboardTaskList() {
  const [rawTasks, setRawTasks]         = useState([])
  const [input, setInput]               = useState('')
  const [showAll, setShowAll]           = useState(false)
  const [archiveOpen, setArchiveOpen]   = useState(false)
  const [confirmClear, setConfirmClear] = useState(false)

  useEffect(() => { api.getTodos().then(rows => setRawTasks(rows || [])) }, [])

  const now = Date.now()

  const active  = rawTasks.filter(t => t.col !== 'done')
  const recent  = rawTasks.filter(t => t.col === 'done' && t.completedAt && (now - t.completedAt) < DONE_TTL)
  const archive = rawTasks.filter(t => t.col === 'done' && t.completedAt && (now - t.completedAt) >= DONE_TTL && (now - t.completedAt) < ARCHIVE_TTL)

  const sorted  = [...active, ...recent]
  const visible = showAll ? sorted : sorted.slice(0, 5)

  function persist(next) {
    setRawTasks(next)
    api.saveTodos(next)
  }

  function addTask() {
    const text = input.trim()
    if (!text) return
    persist([...rawTasks, { id: Date.now(), text, col: 'todo', notes: '', createdAt: Date.now(), completedAt: null }])
    setInput('')
  }

  function toggle(id) {
    const updated = rawTasks.map(t => {
      if (t.id !== id) return t
      const wasDone = t.col === 'done'
      return { ...t, col: wasDone ? 'todo' : 'done', completedAt: wasDone ? null : Date.now() }
    })
    persist(updated)
  }

  function remove(id) {
    persist(rawTasks.filter(t => t.id !== id))
  }

  function updateNotes(id, notes) {
    persist(rawTasks.map(t => t.id === id ? { ...t, notes } : t))
  }

  function clearArchive() {
    persist(rawTasks.filter(t => !(t.col === 'done' && t.completedAt && (now - t.completedAt) >= DONE_TTL)))
    setConfirmClear(false)
  }

  return (
    <>
      <Card label="Tasks" title="What's on today?">
        <div className="todo-add-row">
          <input
            className="todo-add-input"
            placeholder="Add a task and press Enter…"
            maxLength={120}
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') addTask() }}
          />
          <button className="todo-add-btn" onClick={addTask}>Add</button>
        </div>

        {sorted.length > 0 && (
          <p style={{ fontSize: '0.72rem', color: 'var(--stone)', marginBottom: '0.6rem', letterSpacing: '0.05em' }}>
            {recent.length} / {sorted.length} done
          </p>
        )}

        {sorted.length === 0 ? (
          <p style={{ fontSize: '0.85rem', color: 'var(--stone)', fontStyle: 'italic' }}>
            No tasks yet — add one above.
          </p>
        ) : (
          <>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              {visible.map(task => (
                <TaskItem
                  key={task.id}
                  task={task}
                  onToggle={toggle}
                  onRemove={remove}
                  onNotesChange={updateNotes}
                />
              ))}
            </div>
            {sorted.length > 5 && (
              <button
                className="ghost-btn"
                style={{ marginTop: '0.75rem', fontSize: '0.8rem', width: '100%', textAlign: 'center', display: 'block' }}
                onClick={() => setShowAll(v => !v)}
              >
                {showAll ? 'Show less' : `Show ${sorted.length - 5} more`}
              </button>
            )}
          </>
        )}

        {archive.length > 0 && (
          <div style={{ marginTop: '1rem', borderTop: '1px solid rgba(0,0,0,0.07)', paddingTop: '0.75rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
              <button
                className="ghost-btn"
                style={{ fontSize: '0.75rem' }}
                onClick={() => setArchiveOpen(v => !v)}
              >
                {archiveOpen ? '▲' : '▼'} Archive ({archive.length})
              </button>
              <button
                className="ghost-btn"
                style={{ fontSize: '0.72rem', color: 'var(--stone)' }}
                onClick={() => setConfirmClear(true)}
              >
                Clear
              </button>
            </div>
            {archiveOpen && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                {archive.map(task => (
                  <div key={task.id} style={{
                    display: 'flex', alignItems: 'center', gap: '10px',
                    padding: '7px 10px', background: 'var(--surf)',
                    borderRadius: '10px', opacity: 0.5,
                  }}>
                    <span style={{ fontSize: '12px', flexShrink: 0 }}>✓</span>
                    <span style={{ flex: 1, fontSize: '0.85rem', color: 'var(--stone)', textDecoration: 'line-through', wordBreak: 'break-word' }}>
                      {task.text}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </Card>

      <ConfirmDialog
        open={confirmClear}
        title="Clear archive?"
        message="This will permanently delete all archived tasks. This cannot be undone."
        confirmLabel="Clear archive"
        dangerous
        onConfirm={clearArchive}
        onCancel={() => setConfirmClear(false)}
      />
    </>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// Reflection card
// ─────────────────────────────────────────────────────────────────────────────

function ReflectionCard() {
  const [refInput, setRefInput]     = useState('')
  const [refSaved, setRefSaved]     = useState(false)
  const [recent, setRecent]         = useState([])
  const [promptOffset, setPromptOffset] = useState(0)
  const q = pickQuietPrompt(promptOffset)

  useEffect(() => { api.getReflections().then(rows => setRecent(rows || [])) }, [])

  function save() {
    const text = refInput.trim()
    if (!text) return
    const entry   = { date: todayShort(), q, text }
    const updated = [entry, ...recent].slice(0, 12)
    setRecent(updated)
    api.saveReflection(entry)
    setRefInput('')
    setRefSaved(true)
    setTimeout(() => setRefSaved(false), 2000)
  }

  return (
    <Card label="Reflection" title="A quiet moment">
      <div className="reflection-box" style={{ marginTop: 0 }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.6rem', marginBottom: '0.75rem' }}>
          <p className="reflection-q" style={{ flex: 1, marginBottom: 0 }}>"{q}"</p>
          <button
            className="ghost-btn"
            style={{ flexShrink: 0, padding: '3px 9px', fontSize: '0.85rem', lineHeight: 1 }}
            onClick={() => setPromptOffset(o => o + 1)}
            title="Try another prompt"
          >
            ↻
          </button>
        </div>
        <input
          className="reflection-input"
          type="text"
          placeholder="Write something, anything…"
          value={refInput}
          onChange={e => setRefInput(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') save() }}
        />
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginTop: '0.75rem', flexWrap: 'wrap' }}>
          <button className="save-btn ready" onClick={save} style={{ fontSize: '0.8rem', padding: '7px 16px' }}>
            Save reflection
          </button>
          {refSaved && (
            <span style={{ fontSize: '0.82rem', color: 'var(--sage)', fontStyle: 'italic' }}>Saved ✓</span>
          )}
        </div>
        {recent.slice(0, 2).map((r, i) => (
          <div key={i} style={{ borderTop: '1px solid rgba(0,0,0,0.07)', marginTop: '0.75rem', paddingTop: '0.75rem' }}>
            <p style={{ fontSize: '0.72rem', color: 'var(--stone)', marginBottom: 2 }}>{r.date}</p>
            <p style={{ fontSize: '0.83rem', color: 'var(--bark)', fontStyle: 'italic', marginBottom: 2 }}>"{r.q}"</p>
            <p style={{ fontSize: '0.88rem', color: 'var(--ink)', wordBreak: 'break-word' }}>{r.text}</p>
          </div>
        ))}
      </div>
    </Card>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// Progress snapshot — 3 stats only (full Progress.jsx moves to Wellbeing in P3)
// ─────────────────────────────────────────────────────────────────────────────

function ProgressSnapshot({ onNavigate }) {
  const [entries, setEntries] = useState([])

  useEffect(() => { api.getMoods().then(rows => setEntries(rows || [])) }, [])

  const streak      = calcStreak(entries)
  const totalLogged = entries.length

  // Most frequent mood this week (last 7 entries)
  const weekEntries = entries.slice(0, 7)
  let topMood = null
  if (weekEntries.length > 0) {
    const counts = [0, 0, 0, 0, 0]
    weekEntries.forEach(e => { if (e.level >= 0 && e.level < 5) counts[e.level]++ })
    const maxCount = Math.max(...counts)
    topMood = MOOD_NAMES[counts.indexOf(maxCount)]
  }

  return (
    <Card label="Progress" title="Your journey">
      <div className="stat-row" style={{ gridTemplateColumns: 'repeat(3, 1fr)', marginBottom: '1.25rem' }}>
        <div className="stat-box">
          <p className="stat-label">Streak</p>
          <p className="stat-value">{streak || '—'}</p>
          <p className="stat-sub">days in a row</p>
        </div>
        <div className="stat-box">
          <p className="stat-label">This week</p>
          <p className="stat-value" style={{ fontSize: topMood ? '1rem' : '1.6rem', lineHeight: topMood ? 1.4 : 1 }}>
            {topMood ? `Mostly ${topMood.toLowerCase()}` : '—'}
          </p>
          <p className="stat-sub">{topMood ? 'top mood' : 'no data yet'}</p>
        </div>
        <div className="stat-box">
          <p className="stat-label">Days logged</p>
          <p className="stat-value">{totalLogged || '—'}</p>
          <p className="stat-sub">total check-ins</p>
        </div>
      </div>

      <button
        className="ghost-btn"
        style={{ fontSize: '0.82rem' }}
        onClick={() => onNavigate && onNavigate('wellbeing/trends')}
      >
        View full progress →
      </button>
    </Card>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// Main Dashboard
// ─────────────────────────────────────────────────────────────────────────────

export default function Today({ onNavigate }) {
  const quote     = getDailyQuote()
  const greeting  = getGreeting()
  const { user }  = useAuth()
  const firstName = user?.display_name?.split(' ')[0] || null
  const [activeSection, setActiveSection] = useState('checkin')

  // Scroll-based active section tracking
  useEffect(() => {
    function update() {
      const OFFSET = 128 // nav (56) + sticky bar (~60) + buffer
      let current = SECTIONS[0].id
      for (const { id } of SECTIONS) {
        const el = document.getElementById(id)
        if (!el) continue
        if (el.getBoundingClientRect().top <= OFFSET) current = id
      }
      setActiveSection(current)
    }
    update()
    window.addEventListener('scroll', update, { passive: true })
    return () => window.removeEventListener('scroll', update)
  }, [])

  return (
    <div className="page active" id="page-today">
      <div className="page-inner">

        {/* Greeting — standalone H1 */}
        <div className="hero" style={{ marginBottom: '1rem' }}>
          <p className="hero-eyebrow">{todayShort()}</p>
          <h1>{firstName ? personalizeGreeting(greeting, firstName) : greeting}</h1>
          <hr className="hero-rule" />
        </div>

        {/* Daily quote */}
        <div className="quote-bar" style={{ marginBottom: '0.75rem' }}>
          <span>"{quote.t}"</span>
          <span className="quote-attr">— {quote.a}</span>
        </div>

        {/* Sticky section navigation */}
        <DashboardSectionNav activeSection={activeSection} />

        {/* ── Check-in ── */}
        <section id="checkin" className="dashboard-section">
          <MoodTracker />
        </section>

        {/* ── Today's Focus ── */}
        <section id="focus" className="dashboard-section">
          <TodaysFocus onNavigate={onNavigate} />
          <ReadingPrompt onNavigate={onNavigate} />
        </section>

        {/* ── Tasks ── */}
        <section id="tasks" className="dashboard-section">
          <DashboardTaskList />
        </section>

        {/* ── Reflection ── */}
        <section id="reflection" className="dashboard-section">
          <ReflectionCard />
        </section>

        {/* ── Progress ── */}
        <section id="progress" className="dashboard-section">
          <ProgressSnapshot onNavigate={onNavigate} />
        </section>

      </div>
    </div>
  )
}
